/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { useLocation } from 'react-router';

import { useAuthState } from 'shared-ui/auth';
import { logout } from 'shared-ui/auth/session';
import { useTypedDispatch } from 'shared-ui/redux';

export const MOBILE_MENU_TRIGGER_ID = 'mobile-menu-toggle-btn';

export const GUEST_LOGIN_ALLOWED_PATH =
    /^\/(sitepass|cart|account\/session|timed-entry|ticket|activitypass|gateways)/;

export function useLogoutGuestOnDisallowedRoute(isGuestAllowed?: boolean) {
    const dispatch = useTypedDispatch();
    const { pathname } = useLocation();
    const { isGuest } = useAuthState();

    React.useEffect(() => {
        if (!isGuest) return;

        const isAllowed = isGuestAllowed && GUEST_LOGIN_ALLOWED_PATH.test(pathname);

        if (!isAllowed) {
            logout(dispatch);
        }
    }, [isGuest, isGuestAllowed, pathname, dispatch]);
}

// ---------------------------------------------------------------------------------------
export function useIsStickyStuck(el: HTMLElement | null) {
    const [isPinned, setPinned] = React.useState(false);

    React.useEffect(() => {
        // If IntersectionObserver is not supported, return early
        if (typeof IntersectionObserver === 'undefined') return;

        const observer = new IntersectionObserver(
            ([e]) => {
                setPinned(e.intersectionRatio < 1);
            },
            { threshold: [1] }
        );

        if (el) {
            observer.observe(el);
        }

        return () => {
            observer.disconnect();
        };
    }, [el]);

    return isPinned;
}

// cSpell:ignore sitepass, activitypass
